import React, { Fragment } from 'react';
import { isEmpty } from 'lodash';
import { ColorBlend } from '../../../utils/color-blend.js';
import {
   COLOR_BACKGROUND,
   COLOR_ADD_DIM,
   COLOR_QUALITATIVE_CATEGORICAL,
} from '../chart-colors.js';
import {
   BRUSH_POSITION_END,
   CHART_X_AXIS_PADDING_LEFT,
   CHART_X_AXIS_PADDING_RIGHT,
   getBrushProps,
} from '../chart-util.js';

export const getBarChartConfig = (configChart, chartData) => {
   const {
      chartWidth = '100%',
      chartHeight = 300,
      configBars: {
         plotBars = [],
         isHighLightBarOnClick = false,
         isHighLightCellOnClick = false,
         isStackedBars = false,
         barSize = 20,
         barStrokes = COLOR_QUALITATIVE_CATEGORICAL,
         barStrokeIndex = plotBars.length < 4 ? '3' : plotBars.length,
         isBarRoundedEdge = false,
         isSetBarBackground = false,
         barBackgroundColor = isSetBarBackground
            ? COLOR_BACKGROUND
            : 'transparent',
         barOnClickHandler = () => {},
      },
      configXAxis: { xAxisKey, xAxisTickFormatter },
      configYAxis: { yAxisTickFormatter },
      configXAxisGroup: {
         groupKey = null,
         groupTickFormatter = (tick) => tick,
         groupTick = defaultGroupTick,
      } = {},
      configLegends: {
         legendLabels,
         legendIcon = isBarRoundedEdge ? 'circle' : 'square',
         isLegendTextColorSameAsChartColor = false,
         isToggleChartOnClickOfLegend = false,
      } = {},
      isVisibleHorizontalGrid = true,
      isBrushVisible = false,
      tooltipContent,
      /* TODO: create custom tooltip */
      // tooltipContent = <CustomTooltip />
   } = configChart || {};

   return {
      chartWidth,
      chartHeight,
      plotBars,
      isHighLightBarOnClick,
      isHighLightCellOnClick,
      isStackedBars,
      barSize,
      barStrokes: barStrokes[barStrokeIndex]
         ? barStrokes[barStrokeIndex]
         : barStrokes['default'],
      isBarRoundedEdge,
      barBackgroundColor,
      barOnClickHandler,
      xAxisKey,
      xAxisTickFormatter,
      yAxisTickFormatter,
      groupKey,
      groupTickFormatter,
      groupTick,
      legendLabels,
      legendIcon,
      legendTextColor: isLegendTextColorSameAsChartColor ? '' : '#000000',
      isToggleChartOnClickOfLegend,
      isVisibleHorizontalGrid,
      tooltipContent,
      brush: isBrushVisible
         ? {
              ...getBrushProps(BRUSH_POSITION_END, 30, chartData.length),
              tickFormatter: (tickValue) => tickValue,
           }
         : {},
      getBarEdge: (barKeys, isRounded) => (barRank) => {
         if (!isRounded) {
            return [0, 0, 0, 0];
         }
         if (barKeys.length === 1 || barRank === -1 || !isStackedBars) {
            return [barSize, barSize, barSize, barSize];
         }

         switch (barRank) {
            case 0:
               return [0, 0, barSize, barSize];
            case barKeys.length - 1:
               return [barSize, barSize, 0, 0];
            default:
               return [0, 0, 0, 0];
         }
      },
   };
};

export const getBarCellBackgroundFill =
   (isStackedBars, barBackgroundColor, getBarEdgesStyle) => (barIterateIndex) =>
      !isStackedBars || barIterateIndex === 0
         ? { fill: barBackgroundColor, radius: getBarEdgesStyle(-1) }
         : null;

export const getBarCellFill =
   (selectedBarData, isHighLightOnClickOfBar, isHighLightCellOnClick) =>
   (cellIndex, cellId, fillColor) => {
      if (
         isEmpty(selectedBarData) ||
         (!isHighLightOnClickOfBar && !isHighLightCellOnClick)
      ) {
         return fillColor;
      }

      const dimBarColor = ColorBlend.mix(
         fillColor,
         COLOR_ADD_DIM.colorMix,
         COLOR_ADD_DIM.weight
      );
      if (isHighLightCellOnClick) {
         return selectedBarData.cellId !== cellId ? dimBarColor : fillColor;
      } else if (isHighLightOnClickOfBar) {
         return selectedBarData.cellIndex === cellIndex
            ? fillColor
            : dimBarColor;
      }

      return fillColor;
   };

const defaultGroupTick = (data) => (tickProps) => {
   if (isEmpty(data)) {
      return;
   }

   const {
      x,
      y,
      payload,
      index: dataIndex,
      visibleTicksCount,
      width,
   } = tickProps;

   const { value, offset } = payload;
   const chartOffset = getBarChartOffset(width, visibleTicksCount);
   const isShowGroupedTick =
      dataIndex !== 0 &&
      data[dataIndex + 1] &&
      value !== data[dataIndex + 1].group;
   const textIndex = dataIndex === 0 ? dataIndex : dataIndex + 1;
   const svgWidth = getSVGWidth(
      data[textIndex] && data[textIndex].groupSize,
      chartOffset
   );
   const pathX = Math.floor(x + offset);

   const path = isShowGroupedTick ? (
      <path d={`M${pathX},${y}v${-38}`} stroke='gray' />
   ) : null;

   const svg =
      isShowGroupedTick || dataIndex === 0 ? (
         <svg width={svgWidth} y={y - 10} x={x}>
            <text
               x='50%'
               y='10'
               alignmentBaseline='middle'
               textAnchor='middle'
               fontSize={14}
            >
               {dataIndex === 0 ? value : data[dataIndex + 1].group}
            </text>
         </svg>
      ) : null;

   return (
      <Fragment>
         {svg}
         {path}
      </Fragment>
   );
};

const getBarChartOffset = (chartWidth, numberOfTicks) =>
   (chartWidth - (CHART_X_AXIS_PADDING_LEFT + CHART_X_AXIS_PADDING_RIGHT)) /
   numberOfTicks;

const getSVGWidth = (groupSize, chartOffset) => groupSize * chartOffset;
